import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import { BudgetWarningAckAttributes } from '@callmart/types';
import { QueryKey } from '../../enums/QueryKey';
import {
    getBillingConfigs,
    getGlobalBillingConfigs,
    getMissingBillingConfigs,
    getMissingBillingConfigsCount,
    MissingBillingConfigRow,
    RefreshSourceDataResponse,
    createBillingConfig,
    bulkCreateBillingConfigs,
    updateBillingConfig,
    updateBillingConfigsBulk,
    deleteBillingConfig,
    BillingConfigEdit,
    BillingConfigWithRelations,
    calculateBudget,
    getCalculationResults,
    getCalculationSummary,
    updateBaselines,
    refreshSourceData,
    importBillingConfigs,
    downloadBillingConfigImportTemplate,
    updateForecastVolumesBulk,
    getWarningAcks,
    saveWarningAck,
    deleteWarningAck,
    ForecastVolumeEdit,
    MonthEdit,
    BudgetCalculationResultWithConfig,
    BudgetCalculationSummary,
    BudgetCalculateResponse,
    ImportBillingConfigsResponse,
} from '../../services/budgetBillingConfig';
import { downloadBlobFile } from '../../utils/fileUtils';
import { queryClient } from '../../services/queryClient';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { useDispatchSuccessfullySavedRecord } from '../useDispatchSuccessfullySavedRecord';
import { useAlertMessage } from '../useAlertMessage';
import { useCreateRecord, useUpdateRecord, useDeleteRecord } from './crudOperations';

// A shared empty list, so a still-loading query hands its consumers the same array reference on
// every render instead of invalidating every `useMemo` keyed on it.
const EMPTY_CONFIGS: BillingConfigWithRelations[] = [];
const EMPTY_MISSING_ROWS: MissingBillingConfigRow[] = [];
// The summary's months are the acknowledgement axis (see useRowAckInfo), so a fresh array here
// rebuilt the ack map, the Overview's rowData and its forced refreshCells on every render.
const EMPTY_SUMMARY: BudgetCalculationSummary = [];

export function useBillingConfigs(
    versionId: number | undefined
): [BillingConfigWithRelations[], boolean, { isError: boolean; error: Error | null; refetch: () => void }] {
    const hasVersionId = versionId != null;

    const {
        data: configs,
        isLoading,
        isFetching,
        isFetchedAfterMount,
        isError,
        error,
        refetch,
    } = useQuery<BillingConfigWithRelations[], Error>({
        queryKey: [QueryKey.BudgetBillingConfigs, versionId],
        queryFn: () => (versionId != null ? getBillingConfigs(versionId) : Promise.resolve([])),
        initialData: [],
        enabled: hasVersionId,
    });

    return [
        configs,
        isFetchedAfterMount ? isLoading : isFetching,
        {
            isError,
            error: error ?? null,
            refetch: () => {
                if (hasVersionId) refetch();
            },
        },
    ];
}

// CALL-745: billing config + prices/ABT/bonuses are global master data, read without a version
// for the config & prices pages. CALL-758: scoped to the selected bucket — each bucket caches
// under its own key; create/update/delete/import invalidate the whole [BudgetBillingConfigs]
// prefix so every bucket's view refetches.
export function useGlobalBillingConfigs(
    bucketId?: number
): [BillingConfigWithRelations[], boolean, { isError: boolean; error: Error | null; refetch: () => void }] {
    const { data, isLoading, isPreviousData, isError, error, refetch } = useQuery<BillingConfigWithRelations[], Error>({
        queryKey: [QueryKey.BudgetBillingConfigs, 'global', bucketId],
        // CALL-758: an undefined bucketId is the "All buckets" view — the backend returns every
        // bucket's configs, so the query must run (not stay disabled) or the grid shows nothing.
        queryFn: () => getGlobalBillingConfigs(bucketId),
        // CALL-794: no `initialData: []`. Seeding the cache with an empty list starts the query in
        // `success`, so `isLoading` was false from the very first render and never once became true
        // — and the grid, which IS mounted throughout, was told "not loading, no rows" for the
        // seconds the request takes and answered with its no-rows overlay ("no billing
        // configurations yet, use Add Record"). A definitive wrong answer, not a wait.
        keepPreviousData: true,
    });

    return [
        data ?? EMPTY_CONFIGS,
        // A bucket switch is a new query key, so `data` would drop to the previous bucket's rows
        // (keepPreviousData) with `isLoading` false — the numbers on screen belong to the bucket the
        // user just left, so they have to be marked stale. `isPreviousData` covers exactly that and
        // nothing else: a background refetch after a cell edit keeps its own data and stays quiet,
        // rather than throwing a loading overlay over the grid mid-edit.
        isLoading || isPreviousData,
        { isError, error: error ?? null, refetch: () => refetch() },
    ];
}

// CALL-745: billing config is global master data — created/updated/deleted without a version.
// Mutations invalidate the whole [BudgetBillingConfigs] prefix so the global list AND every
// version's view (keyed [BudgetBillingConfigs, versionId | 'global']) refetch.
export function useCreateBillingConfig({
    onSettled,
}: UseMutationOptions<BillingConfigWithRelations, Error, Record<string, unknown>>) {
    const { t } = useTranslation('budget');
    return useCreateRecord<BillingConfigWithRelations, Error, Record<string, unknown>>({
        createRecordFn: (data) => createBillingConfig(data),
        fetchRecordsQueryKey: [QueryKey.BudgetBillingConfigs],
        recordName: t('billingConfig.title'),
        onSettled,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBillingConfigs] });
        },
    });
}

// CALL-780: the "Complete missing entries" preview — active Strategic Volume combinations with no
// config in this bucket. Keyed under the [BudgetBillingConfigs] prefix, so every config write already
// invalidates it. Fetched only while the dialog is open, so the page pays nothing for the feature.
export function useMissingBillingConfigs(bucketId: number | undefined, enabled = false) {
    const { data, isFetching, isError, error, refetch } = useQuery<MissingBillingConfigRow[], Error>({
        queryKey: [QueryKey.BudgetBillingConfigs, 'missing', bucketId],
        queryFn: () => getMissingBillingConfigs(bucketId!),
        enabled: enabled && bucketId != null,
        refetchOnWindowFocus: false,
    });

    return {
        rows: data ?? EMPTY_MISSING_ROWS,
        // isFetching, not isLoading: reopening the dialog renders cached rows while React Query
        // revalidates them, and confirming a row someone else has meanwhile configured just earns a
        // unique-constraint 4xx. Wait for the revalidation instead.
        isFetching,
        isError,
        error: error ?? null,
        refetch,
    };
}

// CALL-780: creates the selected preview rows in one transaction, reusing the bulk endpoint.
export function useBulkCreateBillingConfigs(versionId?: number) {
    const { t } = useTranslation('budget');
    return useCreateRecord<BillingConfigWithRelations[], Error, Record<string, unknown>[]>({
        createRecordFn: (configs) => bulkCreateBillingConfigs(configs, versionId),
        fetchRecordsQueryKey: [QueryKey.BudgetBillingConfigs],
        recordName: t('billingConfig.title'),
        // Empty on purpose, and required: useCreateRecord's default onSuccess APPENDS the mutation
        // result to the cached list, which for a bulk response would push an array into the row list.
        // Providing any handler suppresses that; the refetch belongs in onSettled, below.
        onSuccess: () => {},
        // ONE invalidation, covering success and failure alike. Invalidating in both hooks made every
        // successful create start the global + missing refetches twice — React Query restarts the
        // first, but neither queryFn consumes its abort signal, so both expensive DB/HEMT round trips
        // still ran. Failure needs it too: the bulk insert is transactional, so if another user
        // configured one previewed combination first the whole request rolls back, and the dialog has
        // to drop that now-existing row before a retry instead of resubmitting a certain conflict.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBillingConfigs] });
        },
    });
}

// CALL-779: the Overview's batch save for the Handlingdaten grid. Invalidates the same three keys the
// per-config version did — the volumes feed both the summary and the calculation results.
export function useUpdateForecastVolumesBulk(versionId: number | undefined) {
    const { t } = useTranslation('budget');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<unknown, Error, ForecastVolumeEdit[]>({
        mutationFn: (configs) => updateForecastVolumesBulk(versionId!, configs),
        onSuccess: () => {
            dispatchSuccessfullySavedRecord(t('entity.forecastVolumes'));
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBillingConfigs, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetCalculationResults, versionId] });
        },
    });
}

export function useUpdateBillingConfig({
    onSettled,
}: UseMutationOptions<BillingConfigWithRelations, Error, { id: number; [key: string]: unknown }> = {}) {
    const { t } = useTranslation('budget');
    return useUpdateRecord<BillingConfigWithRelations, Error, { id: number; [key: string]: unknown }>({
        updateRecordFn: updateBillingConfig,
        fetchRecordsQueryKey: [QueryKey.BudgetBillingConfigs],
        recordName: t('billingConfig.title'),
        onSettled,
    });
}

// CALL-779: the Billing Config and Prices & ABT pages post their whole pending set here. Settling
// invalidates the [BudgetBillingConfigs] prefix like every other write — including after a failure, so
// the grid shows server truth again while the page keeps the pending edits for a retry.
export function useUpdateBillingConfigsBulk({
    onSettled,
}: UseMutationOptions<BillingConfigWithRelations[], Error, BillingConfigEdit[]> = {}) {
    const { t } = useTranslation('budget');
    return useUpdateRecord<BillingConfigWithRelations[], Error, BillingConfigEdit[]>({
        updateRecordFn: updateBillingConfigsBulk,
        fetchRecordsQueryKey: [QueryKey.BudgetBillingConfigs],
        recordName: t('billingConfig.title'),
        onSettled,
    });
}

export function useDeleteBillingConfig({ onSettled }: UseMutationOptions<unknown, Error, number> = {}) {
    const { t } = useTranslation('budget');
    return useDeleteRecord<unknown, Error, number>({
        deleteRecordFn: deleteBillingConfig,
        fetchRecordsQueryKey: [QueryKey.BudgetBillingConfigs],
        recordName: t('billingConfig.title'),
        onSettled,
    });
}

export function useImportBillingConfigs(versionId: number | undefined) {
    return useMutation<ImportBillingConfigsResponse, Error, File>({
        mutationFn: (file) => importBillingConfigs(versionId!, file),
        onSuccess: () => {
            // Import writes GLOBAL config rows + prices/ABT/bonuses, so invalidate the whole
            // [BudgetBillingConfigs] prefix — both the global grid ([.., 'global']) and every
            // version view ([.., versionId]) must refetch (mirrors create/update/delete).
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBillingConfigs] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetCalculationResults, versionId] });
        },
    });
}

// CALL-750: download the sample import template (server-built .xlsx) and save it via the browser.
export function useDownloadBillingConfigImportTemplate() {
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    return useMutation<Blob, Error, 'full' | 'simple'>({
        mutationFn: (variant) => downloadBillingConfigImportTemplate(variant),
        onSuccess: (data, variant) => {
            downloadBlobFile({
                data,
                fileName:
                    variant === 'simple'
                        ? 'billing-config-simple-import-template.xlsx'
                        : 'billing-config-import-template.xlsx',
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

export function useBudgetCalculationResults(versionId: number | undefined) {
    const hasVersionId = versionId != null;

    const { data, isLoading, isError, error } = useQuery<BudgetCalculationResultWithConfig[], Error>({
        queryKey: [QueryKey.BudgetCalculationResults, versionId],
        queryFn: () => (versionId != null ? getCalculationResults(versionId) : Promise.resolve([])),
        enabled: hasVersionId,
    });

    return { data: data || [], isLoading, isError, error };
}

export function useBudgetSummary(versionId: number | undefined, enabled = true) {
    const hasVersionId = versionId != null;

    const { data, isLoading, isError, error } = useQuery<BudgetCalculationSummary, Error>({
        queryKey: [QueryKey.BudgetSummary, versionId],
        queryFn: () =>
            versionId != null ? getCalculationSummary(versionId) : Promise.resolve({} as BudgetCalculationSummary),
        enabled: enabled && hasVersionId,
    });

    return { data: data ?? EMPTY_SUMMARY, isLoading, isError, error };
}

export function useUpdateBudgetBaselines(versionId: number | undefined) {
    const { t } = useTranslation('budget');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<unknown, Error, MonthEdit[]>({
        mutationFn: (baselineEdits) => updateBaselines(versionId!, baselineEdits),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBaselines, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
            dispatchSuccessfullySavedRecord(t('entity.baselines'));
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

export function useCalculateBudget(versionId: number | undefined) {
    const { t } = useTranslation('budget');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();
    const { dispatchAlert } = useAlertMessage();

    return useMutation<BudgetCalculateResponse, Error, void>({
        mutationFn: () => calculateBudget(versionId!),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetCalculationResults, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBillingConfigs, versionId] });
            if (data.resultCount > 0) {
                dispatchSuccessfullySavedRecord(t('entity.calculation'));
            } else {
                dispatchAlert({ message: data.message, type: 'warning' });
            }
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

// CALL-816: acknowledgements of the Overview's completeness warning, shared by all users of a
// version. The list is small (≤ one row per billing config), so one query per version suffices.
const EMPTY_ACKS: BudgetWarningAckAttributes[] = [];

/** `isLoaded` says the list is the server's answer and not just "nothing yet" — the caller MUST gate
 *  both the acknowledged state and the acknowledge action on it. An unloaded (or failed) list is
 *  indistinguishable from an empty one, so an already-acknowledged row would render unacknowledged and
 *  stay clickable, and confirming it would overwrite the original acknowledger and timestamp. It stays
 *  true across background refetches, which keep serving the last good list. */
export function useWarningAcks(versionId: number | undefined): {
    acks: BudgetWarningAckAttributes[];
    isLoaded: boolean;
} {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { data, isSuccess, isError, error } = useQuery<BudgetWarningAckAttributes[], Error>({
        queryKey: [QueryKey.BudgetWarningAcks, versionId],
        queryFn: () => getWarningAcks(versionId!),
        enabled: versionId != null,
    });
    // A failed list leaves every acked row rendering as a plain (inert) warning — visually
    // "unacknowledged" with nothing saying why — so the failure has to be said out loud. An effect
    // rather than the query's onError, which the lint bans as removed in React Query v5.
    useEffect(() => {
        if (isError) dispatchErrorNotification({ message: error.message });
    }, [isError, error, dispatchErrorNotification]);
    return { acks: data ?? EMPTY_ACKS, isLoaded: isSuccess };
}

export function useSaveWarningAck(versionId: number | undefined) {
    const { t } = useTranslation('budget');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<BudgetWarningAckAttributes, Error, { billingConfigId: number; missingMonths: string[] }>({
        mutationFn: ({ billingConfigId, missingMonths }) => saveWarningAck(versionId!, billingConfigId, missingMonths),
        onSuccess: () => {
            dispatchSuccessfullySavedRecord(t('entity.acknowledgement'));
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetWarningAcks, versionId] });
        },
    });
}

export function useDeleteWarningAck(versionId: number | undefined) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    return useMutation<{ success: boolean }, Error, { billingConfigId: number }>({
        mutationFn: ({ billingConfigId }) => deleteWarningAck(versionId!, billingConfigId),
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetWarningAcks, versionId] });
        },
    });
}

export function useRefreshSourceData(versionId: number | undefined) {
    const { t } = useTranslation('budget');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();
    const { dispatchAlert } = useAlertMessage();

    return useMutation<RefreshSourceDataResponse, Error, void>({
        mutationFn: () => refreshSourceData(versionId!),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBillingConfigs, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetCalculationResults, versionId] });
            // CALL-820: rows the reload could not fill (working unit without strategic volumes) turn
            // success into a warning naming the count — the Overview's inline alert lists the rows.
            if (data.orphanedConfigIds?.length) {
                dispatchAlert({
                    message: t('hint.reloadOrphanedCount', { count: data.orphanedConfigIds.length }),
                    type: 'warning',
                });
            } else if (data.updatedConfigs > 0) {
                dispatchSuccessfullySavedRecord(t('entity.sourceData'));
            } else {
                dispatchAlert({ message: data.message, type: 'warning' });
            }
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

// CALL-820: the badge count on "Complete missing entries" — active Strategic Volume combinations
// without a config anywhere. Cheap on the server (DB only, no HEMT), fetched eagerly per bucket and
// kept under the [BudgetBillingConfigs] prefix so every config write refreshes it. May exceed the
// dialog's row count (unresolvable-location combinations are dropped from the listing only).
export function useMissingBillingConfigsCount(bucketId: number | undefined) {
    const { data } = useQuery<number, Error>({
        queryKey: [QueryKey.BudgetBillingConfigs, 'missing-count', bucketId],
        queryFn: () => getMissingBillingConfigsCount(bucketId!),
        enabled: bucketId != null,
        refetchOnWindowFocus: false,
    });
    // On error this yields 0 on purpose: MUI hides a zero badge and the tooltip gates on count > 0,
    // so a failed count degrades to "no badge" rather than a claim — the dialog itself is the loud
    // error surface (load error + Retry) when it is actually opened.
    return data ?? 0;
}
